import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../state/auth.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';
import { QrButton } from '../components/QrButton.js';
import type { AccessMode, ProposalSummaryResponse } from '@cloudbtl/shared';

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

export function DashboardPage(): JSX.Element {
  const t = useT();
  const auth = useAuth();
  const { proposalId = '' } = useParams<{ proposalId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const key = params.get('key');
  const [data, setData] = useState<ProposalSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 인증 문제(미로그인/권한없음)와 진짜 오류를 가른다 — 대시보드는 소유자 key 또는
  // 소유자/편집자 세션이 있어야 열린다. key 없이 익명으로 맨 URL 을 열면 서버가
  // 'Invalid dashboard key' 403 을 주는데, 그 문구는 원인("로그인 안 됨")을 가린다.
  const [needsAuth, setNeedsAuth] = useState(false);

  useDocumentMeta({
    title: data?.proposal.title
      ? `${data.proposal.title} · ${t({ ko: '대시보드', en: 'Dashboard' })}`
      : t({ ko: '대시보드', en: 'Dashboard' }),
    noindex: true,
  });

  const load = useCallback(async () => {
    if (!proposalId) return;
    try {
      setNeedsAuth(false);
      setError(null);
      setData(await api.proposalSummary(proposalId, key));
    } catch (err) {
      // key 없이 401/403 → 인증/권한 문제. 세션이 있으면(=이미 로그인) 진짜 권한 부족이니
      // 그대로 오류로, 없으면 로그인 안내로 보낸다.
      const status = err instanceof ApiError ? err.status : 0;
      if (!key && (status === 401 || status === 403)) {
        setNeedsAuth(true);
        setError(err instanceof Error ? err.message : null);
        return;
      }
      setError(err instanceof Error ? err.message : t({ ko: '불러오기에 실패했습니다', en: 'Failed to load' }));
    }
  }, [proposalId, key, t]);

  /** 잘못 보낸 링크의 응급 차단 — 중지는 확인 없이 즉시(삭제와 달리 통계가 남고 재개 가능),
   *  재개는 외부 노출을 다시 여는 권한 부여라 확인 후. 바뀌는 필드만 보낸다(부분 PATCH 계약 —
   *  다른 필드를 실으면 서버의 '안 보낸 필드 무변경'이 무의미해진다).
   *  key 없는 세션 진입(자료실 '열람 분석' 경유)은 빈 키로 호출 — 서버는 org 문서에 ownerKey
   *  축을 인정하지 않아 세션 신원으로만 판정하고, 실제 ownerKey 는 항상 비어있지 않다. */
  async function toggleLinkDisabled(link: { id: string; alias: string; disabled: boolean }): Promise<void> {
    if (
      link.disabled &&
      !window.confirm(
        t({
          ko: `링크 "${link.alias}"을(를) 다시 활성화할까요?\n\n링크를 아는 사람은 즉시 다시 열람할 수 있습니다.`,
          en: `Re-enable link "${link.alias}"?\n\nAnyone with the link can open it again immediately.`,
        }),
      )
    )
      return;
    try {
      await api.updateLink(proposalId, link.id, key ?? '', { disabled: !link.disabled });
      await load();
    } catch (err) {
      window.alert(
        err instanceof Error ? err.message : t({ ko: '링크를 수정할 수 없습니다.', en: 'Could not update link.' }),
      );
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  // 인증/권한 문제 — 원문 'Invalid dashboard key' 대신 원인에 맞는 안내.
  // 미로그인: 로그인 유도(로그인 후 이 페이지로 복귀). 로그인했는데도 막힘: 진짜 권한 부족.
  if (needsAuth) {
    const here = `${window.location.pathname}${window.location.search}`;
    return (
      <Shell>
        <section className="dashboard">
          <nav aria-label={t({ ko: '탐색 경로', en: 'Breadcrumb' })} className="breadcrumb">
            <Link to="/app" className="link">
              ← {t({ ko: '문서로 돌아가기', en: 'Back to documents' })}
            </Link>
          </nav>
          {auth.loading ? (
            <p className="muted">{t({ ko: '확인 중…', en: 'Checking…' })}</p>
          ) : auth.user ? (
            <>
              <h1>{t({ ko: '이 대시보드를 열 권한이 없습니다', en: 'You can’t open this dashboard' })}</h1>
              <p>
                {t({
                  ko: '이 문서의 소유자 또는 편집 권한이 있는 계정만 열람 분석을 볼 수 있습니다. 소유자에게 대시보드 링크(관리 키 포함)를 요청하세요.',
                  en: 'Only the document owner or an account with edit access can see its analytics. Ask the owner for the dashboard link (it includes the management key).',
                })}
              </p>
              {error ? <p className="muted small">{error}</p> : null}
            </>
          ) : (
            <>
              <h1>{t({ ko: '로그인이 필요합니다', en: 'Sign in required' })}</h1>
              <p>
                {t({
                  ko: '이 대시보드는 문서 소유자용 열람 분석입니다. 로그인하면 이 페이지로 돌아옵니다.',
                  en: 'This dashboard is the owner’s reading analytics. Sign in and you’ll return to this page.',
                })}
              </p>
              <p>
                <Link to={`/login?returnTo=${encodeURIComponent(here)}`} className="cta-primary">
                  {t({ ko: '로그인하러 가기', en: 'Go to sign in' })}
                </Link>
              </p>
            </>
          )}
        </section>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <section className="dashboard">
          <nav aria-label={t({ ko: '탐색 경로', en: 'Breadcrumb' })} className="breadcrumb">
            <Link to="/app" className="link">
              ← {t({ ko: '문서로 돌아가기', en: 'Back to documents' })}
            </Link>
          </nav>
          <h1>{t({ ko: '대시보드를 사용할 수 없습니다', en: 'Dashboard unavailable' })}</h1>
          <p>{error}</p>
        </section>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <section className="dashboard">
          <p className="muted">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p>
        </section>
      </Shell>
    );
  }

  const { proposal, summary } = data;

  return (
    <Shell>
      <section className="dashboard">
        <nav aria-label={t({ ko: '탐색 경로', en: 'Breadcrumb' })} className="breadcrumb">
          <button
            type="button"
            className="link"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/app');
            }}
          >
            ← {t({ ko: '뒤로', en: 'Back' })}
          </button>
          <span aria-hidden="true">/</span>
          <Link to="/app">{t({ ko: '문서', en: 'Documents' })}</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{proposal.title}</span>
        </nav>
        <header className="dashboard-head">
          <div>
            <h1>{proposal.title}</h1>
            <p className="muted">{proposal.originalFilename}</p>
          </div>
          {/\.html?$/i.test(proposal.originalFilename) ? (
            <Link
              to={`/edit/${proposalId}${key ? `?key=${encodeURIComponent(key)}` : ''}`}
              className="cta-secondary"
            >
              {t({ ko: 'HTML 편집', en: 'Edit HTML' })}
            </Link>
          ) : null}
        </header>

        <dl className="metric-grid">
          <Metric label={t({ ko: '방문자', en: 'Visitors' })} value={summary.visitors} />
          <Metric label={t({ ko: '세션', en: 'Sessions' })} value={summary.sessions} />
          <Metric label={t({ ko: '이벤트', en: 'Events' })} value={summary.events} />
          <Metric label={t({ ko: '링크', en: 'Links' })} value={summary.links.length} />
        </dl>

        <h2>{t({ ko: '수신자 링크', en: 'Audience links' })}</h2>
        <table className="table">
          <thead>
            <tr>
              <th>{t({ ko: '이름', en: 'Alias' })}</th>
              <th>{t({ ko: '접근', en: 'Access' })}</th>
              <th>{t({ ko: '방문자', en: 'Visitors' })}</th>
              <th>{t({ ko: '열람', en: 'Opens' })}</th>
              <th>{t({ ko: '읽은 시간', en: 'Time read' })}</th>
              <th>{t({ ko: '공유 URL', en: 'Share URL' })}</th>
              {/* 이 페이지 자체가 소유자(키/세션)·폴더 EDITOR+ 만 로드된다 — 작업 열은 항상 노출하고
                  권한 부족(폴더 EDITOR 비소유자 등)은 서버 403 메시지를 그대로 보여준다. */}
              <th>{t({ ko: '작업', en: 'Actions' })}</th>
            </tr>
          </thead>
          <tbody>
            {summary.links.map((link) => (
              <tr key={link.id} data-testid={`dash-link-${link.id}`}>
                <td>{link.alias}</td>
                <td>
                  {link.accessMode}
                  {/* 중지 상태는 접근 모드의 괄호 주석이 아니라 명확한 상태 표기로 */}
                  {link.disabled ? <strong> · {t({ ko: '중지됨', en: 'Stopped' })}</strong> : null}
                </td>
                <td>{link.visitors}</td>
                <td>{link.sessions}</td>
                <td>{formatMs(link.totalMs)}</td>
                <td>
                  <div className="share-cell">
                    <code className="link-snippet">{link.shareUrl}</code>
                    <CopyButton url={link.shareUrl} />
                    <QrButton url={link.shareUrl} alias={link.alias} />
                  </div>
                </td>
                <td>
                  {/* 중지/재개 — 잘못 보낸 링크는 삭제(통계 소실 아님이지만 되돌릴 수 없음) 대신
                      중지가 1차 수단이다. 삭제는 링크 행 자체를 지우므로 확인 필수 유지. */}
                  <button type="button" className="link" onClick={() => void toggleLinkDisabled(link)}>
                    {link.disabled ? t({ ko: '재개', en: 'Enable' }) : t({ ko: '중지', en: 'Disable' })}
                  </button>{' '}
                  <button
                    type="button"
                    className="link danger"
                    onClick={async () => {
                      if (
                        !window.confirm(
                          t({
                            ko: `링크 "${link.alias}"을(를) 삭제할까요?\n\n이 링크를 사용 중인 모든 사람이 즉시 접근 권한을 잃습니다. 문서와 추적된 통계는 유지됩니다.\n(통계를 남긴 채 잠시 끄려면 삭제 대신 "중지"를 쓰세요.)`,
                            en: `Delete link "${link.alias}"?\n\nAnyone using this link loses access immediately. The document and its tracked stats are kept.\n(To pause it while keeping the row, use "Disable" instead.)`,
                          }),
                        )
                      )
                        return;
                      try {
                        await api.deleteLink(proposalId, link.id, key ?? '');
                        await load();
                      } catch (err) {
                        window.alert(
                          err instanceof Error
                            ? err.message
                            : t({ ko: '링크를 삭제할 수 없습니다.', en: 'Could not delete link.' }),
                        );
                      }
                    }}
                  >
                    {t({ ko: '삭제', en: 'Delete' })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {key ? <NewLinkForm proposalId={proposalId} ownerKey={key} onCreated={load} /> : null}

        {summary.readDepth ? (
          <>
            <h2>{t({ ko: '읽기 깊이 (HTML)', en: 'Read depth (HTML)' })}</h2>
            <div className="metric-grid">
              <div className="metric">
                <span className="metric-label">{t({ ko: '평균 도달 깊이', en: 'Avg depth reached' })}</span>
                <span className="metric-value">{summary.readDepth.avgDepthPct}%</span>
              </div>
              <div className="metric">
                <span className="metric-label">{t({ ko: '끝까지 읽음', en: 'Reached the end' })}</span>
                <span className="metric-value">{summary.readDepth.reachedEndPct}%</span>
              </div>
              <div className="metric">
                <span className="metric-label">{t({ ko: '추적 세션', en: 'Sessions tracked' })}</span>
                <span className="metric-value">{summary.readDepth.sessionsTracked}</span>
              </div>
            </div>
            <div
              className="readdepth"
              role="img"
              aria-label={t({ ko: '위치별 체류 히트맵', en: 'Dwell heatmap by position' })}
            >
              {(() => {
                const bands = summary.readDepth.bands;
                const max = Math.max(1, ...bands);
                return bands.map((ms, i) => (
                  <div
                    key={i}
                    className="readdepth-band"
                    title={`${i * 10}–${i * 10 + 10}% · ${(ms / 1000).toFixed(1)}s`}
                  >
                    <div
                      className="readdepth-fill"
                      style={{ height: `${Math.round((ms / max) * 100)}%`, opacity: 0.25 + 0.75 * (ms / max) }}
                    />
                  </div>
                ));
              })()}
            </div>
            <p className="muted small">
              {t({
                ko: '문서를 위→아래 10구간으로 나눈 평균 체류 시간. 높을수록 오래 머문 곳. (막대에 마우스를 올리면 상세)',
                en: 'Average dwell across 10 top-to-bottom bands. Taller = read longer. (Hover a bar for detail.)',
              })}
            </p>
          </>
        ) : null}

        {summary.sections.length > 0 && (
          <>
            <h2>{t({ ko: '섹션별 읽기', en: 'Reading by section' })}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>{t({ ko: '섹션', en: 'Section' })}</th>
                  <th>{t({ ko: '체류 합계', en: 'Total dwell' })}</th>
                  <th>{t({ ko: '평균', en: 'Average' })}</th>
                  <th>{t({ ko: '읽은 사람', en: 'Readers' })}</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const maxMs = Math.max(1, ...summary.sections.map((s) => s.totalMs));
                  return summary.sections.map((s) => (
                    <tr key={s.sectionId}>
                      <td>
                        <div className="section-name">
                          {s.sectionTitle || s.sectionId}
                        </div>
                        <div
                          className="section-bar"
                          style={{
                            width: `${Math.max(2, Math.round((s.totalMs / maxMs) * 100))}%`,
                            height: 4,
                            background: 'var(--accent, #2563eb)',
                            opacity: 0.35 + 0.65 * (s.totalMs / maxMs),
                            borderRadius: 2,
                            marginTop: 4,
                          }}
                        />
                      </td>
                      <td>{formatMs(s.totalMs)}</td>
                      <td>{formatMs(s.avgMs)}</td>
                      <td>
                        {s.visitors}
                        {summary.visitors ? ` / ${summary.visitors}` : ''}
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
            <p className="muted small">
              {t({
                ko: '섹션에서 벗어날 때 체류 시간이 기록됩니다. 막대 = 상대적 체류 합계 — 짧은 구간이 이탈 지점입니다.',
                en: 'Dwell is recorded when a reader leaves a section. Bars show relative total dwell — short bars are drop-off points.',
              })}
            </p>
          </>
        )}

        <h2>{t({ ko: '페이지별 체류 시간', en: 'Per-page dwell' })}</h2>
        <table className="table">
          <thead>
            <tr>
              <th>{t({ ko: '페이지', en: 'Page' })}</th>
              <th>{t({ ko: '열람', en: 'Views' })}</th>
              <th>{t({ ko: '합계', en: 'Total' })}</th>
              <th>{t({ ko: '평균', en: 'Average' })}</th>
            </tr>
          </thead>
          <tbody>
            {summary.pages.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted">
                  {t({ ko: '아직 페이지 단위 활동이 없습니다.', en: 'No page-level activity yet.' })}
                </td>
              </tr>
            ) : (
              summary.pages.map((p) => (
                <tr key={p.pageNumber}>
                  <td>{p.pageNumber}</td>
                  <td>{p.views}</td>
                  <td>{formatMs(p.totalMs)}</td>
                  <td>{formatMs(p.avgMs)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <h2>{t({ ko: '최근 이벤트', en: 'Recent events' })}</h2>
        <table className="table">
          <thead>
            <tr>
              <th>{t({ ko: '시각', en: 'When' })}</th>
              <th>{t({ ko: '유형', en: 'Type' })}</th>
              <th>{t({ ko: '페이지', en: 'Page' })}</th>
              <th>{t({ ko: '지속 시간', en: 'Duration' })}</th>
              <th>{t({ ko: '대상', en: 'Target' })}</th>
              <th>{t({ ko: '방문자', en: 'Visitor' })}</th>
            </tr>
          </thead>
          <tbody>
            {summary.recentEvents.map((event, index) => (
              <tr key={`${event.sessionId}-${index}`}>
                <td>{new Date(event.createdAt).toLocaleString()}</td>
                <td>{event.eventType}</td>
                <td>{event.pageNumber ?? '—'}</td>
                <td>{event.durationMs ? formatMs(event.durationMs) : '—'}</td>
                <td>{event.target ?? '—'}</td>
                <td className="muted small">{event.visitorId ?? event.sessionId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Shell>
  );
}

function CopyButton({ url }: { url: string }): JSX.Element {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="link copy-btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          /* clipboard may be unavailable over insecure contexts */
        }
      }}
    >
      {copied ? t({ ko: '복사됨', en: 'Copied' }) : t({ ko: '복사', en: 'Copy' })}
    </button>
  );
}

function Metric({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="metric">
      <dt className="metric-label">{label}</dt>
      <dd className="metric-value">{value}</dd>
    </div>
  );
}

function NewLinkForm({
  proposalId,
  ownerKey,
  onCreated,
}: {
  proposalId: string;
  ownerKey: string;
  onCreated: () => Promise<void> | void;
}): JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [alias, setAlias] = useState('');
  const [accessMode, setAccessMode] = useState<AccessMode>('public');
  const [allowedDomains, setAllowedDomains] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const body: Record<string, unknown> = { alias: alias.trim() || undefined, accessMode };
      if (accessMode === 'org') {
        body.allowedDomains = allowedDomains;
        if (accessCode.trim()) body.accessCode = accessCode.trim();
      } else if (accessMode === 'passcode') {
        body.accessCode = accessCode.trim();
      }
      await api.createLink(proposalId, ownerKey, body);
      setAlias('');
      setAllowedDomains('');
      setAccessCode('');
      setAccessMode('public');
      setStatus({ tone: 'ok', text: t({ ko: '링크가 생성되었습니다.', en: 'Link created.' }) });
      await onCreated();
    } catch (err) {
      setStatus({
        tone: 'err',
        text: err instanceof Error ? err.message : t({ ko: '링크를 생성할 수 없습니다.', en: 'Could not create link.' }),
      });
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <p>
        <button type="button" className="link" onClick={() => setOpen(true)}>
          + {t({ ko: '새 수신자 링크', en: 'New audience link' })}
        </button>
      </p>
    );
  }

  return (
    <form className="new-link-form" onSubmit={submit}>
      <h3>{t({ ko: '새 수신자 링크', en: 'New audience link' })}</h3>
      <label>
        {t({ ko: '이름 (선택)', en: 'Name (optional)' })}
        <input
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          maxLength={120}
          placeholder={t({ ko: '예: Client Corp', en: 'e.g. Client Corp' })}
        />
      </label>
      <label>
        {t({ ko: '열람 가능 대상', en: 'Who can open it' })}
        <select value={accessMode} onChange={(e) => setAccessMode(e.target.value as AccessMode)}>
          <option value="public">{t({ ko: '링크가 있는 누구나', en: 'Anyone with the link' })}</option>
          <option value="passcode">{t({ ko: '비밀번호(패스코드) 필요', en: 'Passcode required' })}</option>
          <option value="email">{t({ ko: '이메일 입력 후 열람 (누가 봤는지 확인)', en: 'Email required to open (identifies viewers)' })}</option>
          <option value="org">{t({ ko: '조직 구성원만 (Google 인증)', en: 'Organization only (Google verified)' })}</option>
        </select>
      </label>

      {accessMode === 'email' ? (
        <p className="hint">
          {t({
            ko: '열람 전 이메일을 입력받습니다. 입력값은 검증하지 않지만, 누가 열었는지 분석에 남습니다.',
            en: 'Viewers enter an email before opening. It is not verified, but it identifies them in analytics.',
          })}
        </p>
      ) : null}
      {accessMode === 'public' ? (
        <p className="hint">
          {t({
            ko: '누가 열었는지는 익명 방문자로만 집계됩니다. 신원이 필요하면 이메일 입력을 쓰세요.',
            en: 'Viewers stay anonymous in analytics. Use the email option if you need to know who opened it.',
          })}
        </p>
      ) : null}
      {accessMode === 'org' ? (
        <>
          <label>
            {t({ ko: '허용 이메일 도메인', en: 'Allowed email domains' })}
            <input
              required
              value={allowedDomains}
              onChange={(e) => setAllowedDomains(e.target.value)}
              placeholder="clientcorp.com, sweetspot.co.kr"
            />
          </label>
          <p className="muted small">
            {t({
              ko: '방문자는 Google 로그인으로 소속을 인증합니다. 여러 도메인은 쉼표로 구분하세요.',
              en: 'Visitors prove membership by signing in with Google. Comma-separate multiple domains.',
            })}
          </p>
          <label>
            {t({ ko: '비밀번호(패스코드) 대체 수단 (선택)', en: 'Passcode fallback (optional)' })}
            <input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              minLength={6}
              placeholder={t({ ko: 'Google 계정이 없는 수신자용', en: 'For recipients without a Google account' })}
            />
          </label>
        </>
      ) : null}

      {accessMode === 'passcode' ? (
        <label>
          {t({ ko: '비밀번호(패스코드)', en: 'Passcode' })}
          <input
            type="text"
            required
            minLength={6}
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
          />
        </label>
      ) : null}

      <div className="form-actions">
        <button className="cta-primary" type="submit" disabled={busy}>
          {busy ? t({ ko: '생성 중…', en: 'Creating…' }) : t({ ko: '링크 만들기', en: 'Create link' })}
        </button>
        <button type="button" className="link" onClick={() => setOpen(false)}>
          {t({ ko: '취소', en: 'Cancel' })}
        </button>
      </div>
      {status ? <p className={`status ${status.tone}`}>{status.text}</p> : null}
    </form>
  );
}
